// components/views/UserDashboard.js
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../../context/LanguageContext';
import { useTranslation } from '../../i18n/translations';
import { useApp } from '../../context/AppContext';
import { supabase } from '../../config/supabase';
import '../../styles/user-dashboard.css';

const UserDashboard = () => {
    const { language } = useLanguage();
    const { t } = useTranslation(language);
    const { user, userProfile, isAuthenticated, logout } = useApp();
    const navigate = useNavigate();
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const [deleting, setDeleting] = useState(false);
    const [stats, setStats] = useState({
        favoritesCount: 0,
        joinedDate: null
    });
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (!isAuthenticated) {
            navigate('/home');
            return;
        }

        const loadStats = async () => {
            try {
                setLoading(true);
                
                const { data: { user: currentUser } } = await supabase.auth.getUser();
                if (!currentUser) return;

                // Conta preferiti
                const { count, error: countError } = await supabase
                    .from('artist_favorites')
                    .select('*', { count: 'exact', head: true })
                    .eq('user_id', currentUser.id);

                if (countError) {
                    console.error('[UserDashboard] Error counting favorites:', countError);
                }

                // Data iscrizione
                const { data: userData, error: userError } = await supabase
                    .from('users')
                    .select('created_at')
                    .eq('user_id', currentUser.id)
                    .single();

                if (userError && userError.code !== 'PGRST116') {
                    console.error('[UserDashboard] Error loading user data:', userError);
                }

                setStats({
                    favoritesCount: count || 0,
                    joinedDate: userData?.created_at || currentUser.created_at
                });

            } catch (error) {
                console.error('[UserDashboard] Error loading stats:', error);
            } finally {
                setLoading(false);
            }
        };

        loadStats();
    }, [isAuthenticated, navigate]);

    if (!isAuthenticated) {
        return null;
    }

    const handleDeleteAccount = async () => {
        try {
            setDeleting(true);
            
            const { data: { user: currentUser } } = await supabase.auth.getUser();
            if (!currentUser) return;

            // Chiama la funzione database per cancellare l'utente
            const { error } = await supabase.rpc('delete_user_account');

            if (error) {
                console.error('[UserDashboard] Error deleting account:', error);
                alert(language === 'it' 
                    ? 'Errore durante la cancellazione dell\'account. Riprova più tardi.' 
                    : 'Error deleting account. Please try again later.');
                setDeleting(false);
                return;
            }

            // Logout e redirect
            await logout();
            navigate('/home', { replace: true });
        } catch (error) {
            console.error('[UserDashboard] Error:', error);
            alert(language === 'it' 
                ? 'Errore durante la cancellazione dell\'account.' 
                : 'Error deleting account.');
            setDeleting(false);
        }
    };

    const formatDate = (dateString) => {
        if (!dateString) return '-';
        const date = new Date(dateString);
        return date.toLocaleDateString(language === 'it' ? 'it-IT' : 'en-US', {
            year: 'numeric',
            month: 'long',
            day: 'numeric'
        });
    };

    const displayName = userProfile?.display_name || user?.user_metadata?.full_name || user?.email?.split('@')[0] || '';
    const email = user?.email || '';

    if (loading) {
        return null;
    }

    return (
        <div className="user-dashboard-container">
            <h1 className="dashboard-title">
                {language === 'it' ? 'Dashboard' : 'Dashboard'}
            </h1>

            <div className="profile-section">
                <div className="profile-field">
                    <label className="profile-label">
                        {language === 'it' ? 'Nome' : 'Name'}
                    </label>
                    <div className="profile-value">{displayName || '-'}</div>
                </div>

                <div className="profile-field">
                    <label className="profile-label">
                        {language === 'it' ? 'Email' : 'Email'}
                    </label>
                    <div className="profile-value">{email || '-'}</div>
                </div>

                <div className="profile-field">
                    <label className="profile-label">
                        {language === 'it' ? 'Iscritto il' : 'Joined on'}
                    </label>
                    <div className="profile-value">{formatDate(stats.joinedDate)}</div>
                </div>

                <div className="profile-field">
                    <label className="profile-label">
                        {language === 'it' ? 'Artisti salvati' : 'Saved artists'}
                    </label>
                    <div className="profile-value">{stats.favoritesCount}</div>
                </div>
            </div>

            {!showDeleteConfirm ? (
                <button
                    className="delete-account-btn"
                    onClick={() => setShowDeleteConfirm(true)}
                    disabled={deleting}
                >
                    {language === 'it' ? 'Cancella account' : 'Delete account'}
                </button>
            ) : (
                <div className="delete-confirm">
                    <p className="delete-warning">
                        {language === 'it' 
                            ? 'Sei sicuro? Questa azione è irreversibile e cancellerà tutti i tuoi dati.' 
                            : 'Are you sure? This action is irreversible and will delete all your data.'}
                    </p>
                    <div className="delete-actions">
                        <button
                            className="delete-confirm-btn"
                            onClick={handleDeleteAccount}
                            disabled={deleting}
                        >
                            {deleting 
                                ? (language === 'it' ? 'Cancellazione...' : 'Deleting...') 
                                : (language === 'it' ? 'Conferma cancellazione' : 'Confirm deletion')}
                        </button>
                        <button
                            className="delete-cancel-btn"
                            onClick={() => setShowDeleteConfirm(false)}
                            disabled={deleting}
                        >
                            {language === 'it' ? 'Annulla' : 'Cancel'}
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default UserDashboard;
